'use client';

import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { HTMLAttributes, ReactNode } from 'react';

import type { IIconProps } from '@/app/components/ui/Icon';
import Icon from '@/app/components/ui/Icon';
import { Typography } from '@/app/components/ui/Typography';
import type { TIconName } from '@/app/consts/icons';
import { cn } from '@/utils/ui';

const alertVariants = cva(
  'flex max-w-[528px] flex-col items-start justify-start gap-3 rounded-xl border border-solid bg-white p-4',
  {
    variants: {
      variant: {
        default: 'border-gray-400 bg-white',
        success: 'border-green-success bg-green-50',
        warning: 'border-burnt-orange bg-burnt-orange-5',
        error: 'group border-living-coral bg-living-coral-5',
        info: 'border-light-gray-50 bg-white',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);
type AlertVariant = VariantProps<typeof alertVariants>;

const getVariantIconColor = (
  variant: AlertVariant['variant'],
): IIconProps['color'] => {
  switch (variant) {
    case 'success':
      return 'green-success-50-shade';
    case 'error':
      return 'living-coral-700';
    case 'warning':
      return 'burnt-orange-700';
    case 'info':
      return 'gray-60';
    default:
      return undefined;
  }
};

export type AlertProps = HTMLAttributes<HTMLDivElement> &
  AlertVariant & {
    readonly title?: string;
    readonly action?: React.JSX.Element | React.JSX.Element[];
    readonly description?: ReactNode;
    readonly icon: TIconName;
    readonly iconHeight?: number;
    readonly iconWidth?: number;
  };

const Alert = ({
  title,
  description,
  icon,
  iconWidth = 24,
  iconHeight = 24,
  className,
  variant,
  action,
  ...props
}: AlertProps) => {
  return (
    <div className={cn(alertVariants({ variant }), className)} {...props}>
      <div className="flex items-start gap-2">
        <Icon
          color={getVariantIconColor(variant)}
          height={iconHeight}
          icon={icon}
          width={iconWidth}
        />
        <div className="flex flex-col gap-1">
          {title && (
            <Typography
              className="text-helsinki-blue-dark"
              responsiveSize="bodyMedium"
            >
              {title}
            </Typography>
          )}
          {description && (
            <Typography className="text-gray-60" responsiveSize="bodyMedium">
              {description}
            </Typography>
          )}
        </div>
      </div>
      {action && <div className="ml-7 flex items-center gap-4">{action}</div>}
    </div>
  );
};

export { Alert };
